import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { authAPI } from '../services/api';
import { EnvelopeSimple, LockKey, Phone, SignIn, ArrowRight, PaperPlaneTilt, ShieldCheck, ArrowLeft, Translate } from '@phosphor-icons/react';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const { slug } = useParams();
  const [agence, setAgence] = useState(null); // branding pre-charge si slug present
  const [agenceLoading, setAgenceLoading] = useState(!!slug);
  const [devCode, setDevCode] = useState('');
  const [step, setStep] = useState(2); // OTP désactivé - connexion directe
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [telephone, setTelephone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [loading, setLoading] = useState(false);
  // 2FA TOTP
  const [twoFATicket, setTwoFATicket] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const timerRef = useRef(null);
  const { login, verify2FA } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setTimeout(() => setCountdown(c => c - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [countdown]);

  // Charge le branding agence si on est sur /a/:slug/login
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/agence/${encodeURIComponent(slug)}`);
        if (!cancelled) {
          setAgence(res.data);
          // Title de l'onglet brandee (zero mention TripNova)
          if (res.data && res.data.raison_sociale) {
            document.title = res.data.raison_sociale;
          }
        }
      } catch (e) {
        if (!cancelled) setAgence({ _notFound: true });
      } finally {
        if (!cancelled) setAgenceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handleSendOtp = async () => {
    if (!email.trim()) { setError(lang === 'ar' ? 'يرجى إدخال بريد الوكالة' : 'Veuillez saisir l\'email de l\'agence'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.sendOtp(email.trim());
      setOtpSent(true);
      setCountdown(120);
      if (res?.data?.dev_code) {
        setDevCode(res.data.dev_code);
        setOtpCode(res.data.dev_code);
      }
    } catch (err) {
      setError(err.response?.data?.detail || (lang === 'ar' ? 'خطأ في إرسال الرمز' : 'Erreur envoi du code'));
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) { setError(lang === 'ar' ? 'يرجى إدخال الرمز' : 'Veuillez saisir le code'); return; }
    setError('');
    setLoading(true);
    try {
      await authAPI.verifyOtp(email.trim(), otpCode.trim());
      setOtpVerified(true);
      setStep(2);
      setCountdown(0);
    } catch (err) {
      setError(err.response?.data?.detail || (lang === 'ar' ? 'رمز غير صحيح' : 'Code incorrect'));
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode(null);
    setLoading(true);
    const result = await login(telephone, password);
    if (result.success) {
      // Detect 2FA requirement
      if (result.requires_2fa) {
        setTwoFATicket(result.ticket);
        setStep(3); // ouvre l'ecran de saisie code 2FA
        setLoading(false);
        return;
      }
      // Si chef d'agence + wizard pas fait : rediriger vers onboarding plein-ecran
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/status`,
          { withCredentials: true }
        );
        if (r.data?.should_show_wizard) {
          navigate('/onboarding-wizard');
          setLoading(false);
          return;
        }
      } catch (_) { /* fallback dashboard */ }
      navigate('/dashboard');
    } else {
      setError(result.error);
      setErrorCode(result.error_code || null);
    }
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verify2FA(twoFATicket, twoFACode.trim());
    if (result.success) {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/onboarding/status`,
          { withCredentials: true }
        );
        if (r.data?.should_show_wizard) {
          navigate('/onboarding-wizard');
          setLoading(false);
          return;
        }
      } catch (_) { /* fallback */ }
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Left side: Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          {/* Top bar : language toggle */}
          <div className="flex justify-end mb-8">
            <button onClick={toggleLang} data-testid="login-lang-toggle"
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 text-sm font-medium text-stone-700 transition-colors">
              <Translate size={16} />
              {lang === 'fr' ? 'العربية' : 'Français'}
            </button>
          </div>

          <div className="bg-white rounded-3xl border border-stone-100 p-8 md:p-10 shadow-xl shadow-stone-900/5">
            {/* Logo + branding (slug-aware) */}
            {agence && agence._notFound && (
              <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="login-slug-not-found">
                <p className="text-sm text-amber-800 font-medium">
                  {lang === 'ar' ? 'الوكالة غير موجودة. يتم استخدام صفحة تسجيل الدخول العامة.' : 'Agence introuvable. Page de connexion generique affichee.'}
                </p>
              </div>
            )}
            <div className="flex items-center gap-3 mb-8">
              {agence && agence.logo_url ? (
                <img src={`${API_URL}${agence.logo_url}`} alt={agence.raison_sociale} data-testid="login-agence-logo"
                  className="w-12 h-12 rounded-2xl object-cover shadow-lg" />
              ) : (
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg shadow-sky-500/30"
                  style={agence && !agence._notFound && agence.theme_color
                    ? { background: agence.theme_color }
                    : { background: 'linear-gradient(135deg, #0ea5e9, #f97316)' }}>
                  <SignIn size={22} weight="fill" className="text-white" />
                </div>
              )}
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-stone-900" data-testid="login-agence-name">
                  {agence && !agence._notFound ? agence.raison_sociale : 'TripNova'}
                </h1>
                <p className="text-xs text-stone-500">
                  {lang === 'ar' ? 'نظام إدارة الوكالة' : 'Système de gestion'}
                </p>
              </div>
            </div>

            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-2">
              {lang === 'ar' ? 'مرحبا بعودتك' : 'Bon retour 👋'}
            </h2>
            <p className="text-sm text-stone-500 mb-8">
              {lang === 'ar' ? 'سجّل الدخول للوصول إلى لوحة التحكم' : 'Connectez-vous pour accéder à votre tableau de bord'}
            </p>

            {error && errorCode === 'PENDING_APPROVAL' && (
              <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 mb-4 animate-in fade-in slide-in-from-top-1" data-testid="login-pending-approval">
                <div className="flex items-start gap-3">
                  <div className="text-3xl flex-shrink-0">⏳</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-amber-900 text-base mb-1">
                      En attente d'approbation TripNova
                    </h3>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      Votre demande d'inscription a bien été reçue et notre équipe la validera <strong>sous 24h ouvrées</strong>.
                      Vous recevrez un email à votre adresse dès que votre compte sera activé par notre équipe.
                    </p>
                    <p className="text-xs text-amber-700 mt-3">
                      💡 Pour toute question, contactez-nous à
                      <a href="mailto:contact@tripnovacrm.com" className="font-bold underline ml-1">contact@tripnovacrm.com</a>
                    </p>
                  </div>
                </div>
              </div>
            )}
            {error && errorCode === 'REJECTED' && (
              <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-5 mb-4" data-testid="login-rejected">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">❌</div>
                  <div>
                    <h3 className="font-bold text-rose-900 mb-1">Demande rejetée</h3>
                    <p className="text-sm text-rose-800">{error}</p>
                  </div>
                </div>
              </div>
            )}
            {error && !errorCode && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 mb-4 animate-in fade-in slide-in-from-top-1" data-testid="login-error">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            )}

            {/* STEP 2: Phone + Password */}
            {step === 2 && (
              <form onSubmit={handleLogin} className="space-y-5" autoComplete="off">
                {/* Honeypots invisibles : empechent Chrome de pre-remplir les vrais champs */}
                <input type="text" name="fakeusernameremembered" autoComplete="username" style={{display:'none'}} tabIndex={-1} />
                <input type="password" name="fakepasswordremembered" autoComplete="new-password" style={{display:'none'}} tabIndex={-1} />
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-2 uppercase tracking-wider">{t('telephone')}</label>
                  <div className="relative">
                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                    <input type="tel" value={telephone}
                      onChange={e => setTelephone(e.target.value)}
                      data-testid="telephone-input"
                      name="tn_phone_field"
                      autoComplete="off"
                      className="w-full pl-11 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all bg-stone-50 focus:bg-white"
                      placeholder="0555 00 00 00" required autoFocus />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-2 uppercase tracking-wider">{t('mot_de_passe')}</label>
                  <div className="relative">
                    <LockKey size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                    <input type="password" value={password}
                      onChange={e => setPassword(e.target.value)}
                      data-testid="password-input"
                      name="tn_pwd_field"
                      autoComplete="new-password"
                      className="w-full pl-11 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all bg-stone-50 focus:bg-white"
                      placeholder="••••••••" required />
                  </div>
                  <div className="mt-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => setForgotOpen(true)}
                      data-testid="forgot-password-link"
                      className="text-xs font-semibold text-orange-600 hover:text-orange-700 hover:underline"
                    >
                      {lang === 'ar' ? 'نسيت كلمة المرور؟' : 'Mot de passe oublié ?'}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} data-testid="login-submit-button"
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? (
                    <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div> {t('connexion')}...</>
                  ) : (
                    <><SignIn size={18} weight="bold" /> {t('connexion')}</>
                  )}
                </button>
              </form>
            )}

            {/* STEP 3 : 2FA TOTP */}
            {step === 3 && (
              <form onSubmit={handle2FASubmit} className="space-y-5" data-testid="login-2fa-form">
                <div className="text-center mb-2">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-violet-100 flex items-center justify-center">
                    <ShieldCheck size={28} weight="fill" className="text-violet-600" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-stone-900 mt-3">
                    {lang === 'ar' ? 'التحقق بخطوتين' : 'Verification a 2 facteurs'}
                  </h3>
                  <p className="text-sm text-stone-500 mt-1">
                    {lang === 'ar' ? 'أدخل الرمز من تطبيق المصادقة' : "Entrez le code de votre application authenticator"}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-2 uppercase tracking-wider">
                    {lang === 'ar' ? 'الرمز' : 'Code (6 chiffres ou code de recuperation)'}
                  </label>
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\s/g, ''))}
                    data-testid="login-2fa-code-input"
                    className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-stone-200 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none bg-stone-50 focus:bg-white"
                    placeholder="000000"
                    autoFocus
                    maxLength={12}
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setStep(2); setTwoFACode(''); setTwoFATicket(null); setError(''); }}
                    className="flex items-center justify-center gap-1.5 px-4 py-3.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl font-semibold transition-all"
                    data-testid="login-2fa-back-btn"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <button
                    type="submit"
                    disabled={loading || twoFACode.length < 6}
                    data-testid="login-2fa-submit-btn"
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-semibold shadow-lg shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div></>
                    ) : (
                      <><ShieldCheck size={18} weight="bold" /> Valider</>
                    )}
                  </button>
                </div>

                <p className="text-xs text-stone-500 text-center">
                  {lang === 'ar' ? 'فقدت رمز المصادقة؟ استخدم رمز الاسترداد المكون من 10 أحرف.' : 'Vous avez perdu votre app ? Utilisez l\'un de vos 10 codes de recuperation.'}
                </p>
              </form>
            )}

            {/* STEP 1 : OTP (legacy, kept for future activation) */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <ShieldCheck size={28} className="inline-block text-sky-600 mb-1" />
                  <p className="text-sm text-stone-600">{t('verification')}</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-2 uppercase tracking-wider">{lang === 'ar' ? 'بريد الوكالة' : "Email de l'agence"}</label>
                  <div className="relative">
                    <EnvelopeSimple size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                    <input type="email" value={email}
                      onChange={e => { setEmail(e.target.value); setError(''); }}
                      disabled={otpSent && !otpVerified}
                      data-testid="otp-email-input"
                      className="w-full pl-11 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none disabled:bg-stone-100"
                      placeholder="email@agence.dz" />
                  </div>
                </div>

                {!otpSent ? (
                  <button onClick={handleSendOtp} disabled={loading} data-testid="send-otp-button"
                    className="w-full flex items-center justify-center gap-2 py-3.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30 transition-all disabled:opacity-50">
                    {loading ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div></> : <><PaperPlaneTilt size={18} weight="bold" /> {t('envoyer_code')}</>}
                  </button>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-stone-700 mb-2 uppercase tracking-wider">{t('code_otp')}</label>
                      <div className="relative">
                        <LockKey size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
                        <input type="text" value={otpCode} maxLength={6}
                          onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                          data-testid="otp-code-input"
                          className="w-full pl-11 pr-4 py-3 border border-stone-200 rounded-xl text-center text-2xl tracking-[8px] font-bold focus:ring-2 focus:ring-sky-500 outline-none"
                          placeholder="------" autoFocus />
                      </div>
                      {countdown > 0 && <p className={`text-xs font-mono mt-1 ${countdown < 30 ? 'text-red-500' : 'text-emerald-600'}`}>{formatTime(countdown)}</p>}
                    </div>
                    <button onClick={handleVerifyOtp} disabled={loading || otpCode.length < 6} data-testid="verify-otp-button"
                      className="w-full flex items-center justify-center gap-2 py-3.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30 transition-all disabled:opacity-50">
                      <ArrowRight size={18} /> {t('confirmer')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <p className="text-center text-xs text-stone-400 mt-6">
            © {new Date().getFullYear()} {agence && !agence._notFound ? agence.raison_sociale : 'TripNova'} · {lang === 'ar' ? 'صنع بحب في الجزائر' : 'Fait avec ❤️ en Algérie'}
          </p>
        </div>
      </div>

      {/* Right side: Hero image (hidden on mobile) */}
      <div className="hidden lg:block lg:w-[45%] relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1733272967076-3a2ce81226e4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
          alt="Sailboat at Mediterranean sunset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-sky-900/70 via-sky-800/40 to-orange-500/30"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <p className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight mb-4">
              {lang === 'ar' ? 'كل رحلاتكم في مكان واحد' : 'Tous vos voyages, en un seul endroit.'}
            </p>
            <p className="text-base text-sky-50/90 leading-relaxed">
              {lang === 'ar' ? 'منصة CRM حديثة لإدارة وكالة سفر ديناميكية في الجزائر.' : 'CRM moderne pour piloter votre agence de voyage avec énergie et précision.'}
            </p>
          </div>
        </div>
      </div>
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} lang={lang} />
    </div>
  );
};

export default Login;
